import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Gameshow } from '../../schemas/gameshow.js';

import { toPointer } from '../../core/issues.js';
import { GAMESHOW_ROUND_COUNT } from '../../schemas/gameshowConstants.js';
import { hasNonEmptyDialogue } from './_helpers.js';

const RESULTS_ID_RE = /^q\d+-(?:correct|incorrect)-results$/;
const COMPLETION_FLAG = 'completed_gameshow';

export const resultsNodes: Rule<Gameshow> = {
  code: 'gameshow.results-nodes',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const nodesById = new Map(input.nodes.map((n, idx) => [n.id, { node: n, idx }]));
    const results = input.nodes
      .map((node, idx) => ({ node, idx }))
      .filter(({ node }) => RESULTS_ID_RE.test(node.id));

    const expected = GAMESHOW_ROUND_COUNT * 2;
    if (results.length !== expected) {
      const path: (string | number)[] = ['nodes'];
      issues.push({
        code: 'gameshow.results-nodes.count',
        severity: 'error',
        message: `Expected ${expected} results nodes (one correct + one incorrect per round), found ${results.length}.`,
        path,
        pointer: toPointer(path),
        currentValue: results.length,
        constraint: { kind: 'range', detail: `exactly ${expected}` },
      });
    }

    for (let i = 1; i <= GAMESHOW_ROUND_COUNT; i++) {
      for (const branch of ['correct', 'incorrect'] as const) {
        const id = `q${i}-${branch}-results`;
        if (!nodesById.has(id)) {
          const path: (string | number)[] = ['nodes'];
          issues.push({
            code: 'gameshow.results-nodes.missing',
            severity: 'error',
            message: `Missing results node '${id}'.`,
            path,
            pointer: toPointer(path),
            constraint: { kind: 'custom', detail: `node '${id}' required` },
          });
        }
      }
    }

    for (const { node, idx } of results) {
      if (node.activity?.type !== 'base_activity') {
        const path: (string | number)[] = ['nodes', idx, 'activity', 'type'];
        issues.push({
          code: 'gameshow.results-nodes.wrong-activity',
          severity: 'error',
          message: `Node '${node.id}': results node must use activity type 'base_activity', found '${node.activity?.type ?? '(missing)'}'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          currentValue: node.activity?.type,
          constraint: { kind: 'enum', allowedValues: ['base_activity'] },
        });
      }

      if (node.showScoreUpdates !== 'all') {
        const path: (string | number)[] = ['nodes', idx, 'showScoreUpdates'];
        issues.push({
          code: 'gameshow.results-nodes.show-score-updates',
          severity: 'error',
          message: `Node '${node.id}': 'showScoreUpdates' must be 'all'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          currentValue: node.showScoreUpdates,
          constraint: { kind: 'enum', allowedValues: ['all'] },
        });
      }

      if (!hasNonEmptyDialogue(node.scoreUpdateNarratorDialogue)) {
        const path: (string | number)[] = ['nodes', idx, 'scoreUpdateNarratorDialogue'];
        issues.push({
          code: 'gameshow.results-nodes.missing-score-narrator',
          severity: 'error',
          message: `Node '${node.id}': 'scoreUpdateNarratorDialogue' must be a non-empty array.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          constraint: { kind: 'shape', detail: 'scoreUpdateNarratorDialogue required' },
        });
      }

      if (!hasNonEmptyDialogue(node.narratorDialogue)) {
        const path: (string | number)[] = ['nodes', idx, 'narratorDialogue'];
        issues.push({
          code: 'gameshow.results-nodes.missing-narrator',
          severity: 'error',
          message: `Node '${node.id}': results node must have narratorDialogue (host recap).`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          constraint: { kind: 'shape', detail: 'narratorDialogue required' },
        });
      }
    }

    for (const branch of ['correct', 'incorrect'] as const) {
      const id = `q${GAMESHOW_ROUND_COUNT}-${branch}-results`;
      const entry = nodesById.get(id);
      if (!entry) {
        continue;
      }
      const { node, idx } = entry;
      if (node.completeStateFlag !== COMPLETION_FLAG) {
        const path: (string | number)[] = ['nodes', idx, 'completeStateFlag'];
        issues.push({
          code: 'gameshow.results-nodes.complete-state-flag',
          severity: 'error',
          message: `Node '${id}': must set completeStateFlag: '${COMPLETION_FLAG}'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: id },
          currentValue: node.completeStateFlag,
          constraint: { kind: 'enum', allowedValues: [COMPLETION_FLAG] },
        });
      }
      if (typeof node.completeStateFlagReason !== 'string' || !node.completeStateFlagReason.trim()) {
        const path: (string | number)[] = ['nodes', idx, 'completeStateFlagReason'];
        issues.push({
          code: 'gameshow.results-nodes.complete-state-flag-reason',
          severity: 'error',
          message: `Node '${id}': must set non-empty 'completeStateFlagReason'.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: id },
          currentValue: node.completeStateFlagReason,
          constraint: { kind: 'shape', detail: 'non-empty string required' },
        });
      }
    }

    return issues;
  },
};
